import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  BookOpen, RefreshCw, Play, ChevronDown, ChevronRight, Lock, UserX, Clock,
  AlertTriangle, X, ToggleLeft, ToggleRight, PauseCircle, History as HistoryIcon,
} from "lucide-react";
import API from "../../services/api";
import { errMsg, prettyDate, nzToday } from "../runsheets/util";
import { useCanEdit } from "../../services/permissions";
import { fmtHours } from "./util";

/* Drivers' logbook days, read from the Log Ninja operator portal and written
 * onto the timesheet. Hours flow logbook -> here and never back: nothing in
 * this panel touches Log Ninja, it only reads what the drivers signed there.
 *
 * The sync runs on the server (it drives a browser through the portal) and
 * takes minutes, so a press here only STARTS it. The panel then polls the
 * status until the run is over and asks the parent to reload the grid. A
 * preview is the same run without the writes, so the office sees what would
 * change before anything does. A day that disagrees with what is already
 * here is never overwritten quietly - it comes back as a conflict and the
 * office picks a side, one day at a time.
 *
 * Two ways to start a run besides the schedule:
 * - the quick controls read the last N days (the same window the 4 am and
 *   12 pm runs use);
 * - "Get past days" reads a chosen window - the last 3, 7, 14 or 28 days
 *   at a press, or any From/To - for the days the schedule never covered
 *   (it was on hold, or a driver signed a week late). The server refuses a
 *   window wider than MAX_WINDOW_DAYS: a two-month walk through the portal
 *   already takes hours.
 * The schedule itself has a switch here. It starts ON HOLD - the server
 * answers the cron with "skipped" until the office turns it Live - because
 * the operator wants to see the past-days runs land first and only then let
 * the twice-daily writes go on their own.
 */

const DAY_CHOICES = [1, 3, 7, 14];
// 3 s: while a run is on the progress line moves one driver at a time, and a
// slower poll makes the counter look stuck.
const POLL_MS = 3000;
const MAX_WINDOW_DAYS = 62;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Calendar days between two YYYY-MM-DD strings, inclusive. UTC midnight on
// purpose: the strings are dates, not instants, and a local-midnight parse
// would count a clock-change night as 23 or 25 hours.
const dayIndex = (d) => Math.floor(Date.parse(`${d}T00:00:00Z`) / 86400000);
const isoFromIndex = (i) => new Date(i * 86400000).toISOString().slice(0, 10);
export const windowLength = (since, until) => dayIndex(until) - dayIndex(since) + 1;
// The window of the last `n` calendar days ending today: 3 days = today and
// the two before it (the operator's presets, 6 Oct 2026: 3, 7, 14, 28 days).
export const lastDays = (n, today = nzToday()) => ({ since: isoFromIndex(dayIndex(today) - (n - 1)), until: today });
export const PAST_DAY_PRESETS = [3, 7, 14, 28];

// Why the panel will not send this window - null when it may. The server
// checks the same rules (400), but a sentence beside the dates beats a red
// box after the press.
export const windowProblem = (since, until, today = nzToday()) => {
  if (!DATE_RE.test(since || "") || !DATE_RE.test(until || "")) return "Pick a From and a To date";
  if (since > until) return "From is after To";
  if (until > today) return "To is in the future - there is nothing in Log Ninja there yet";
  const n = windowLength(since, until);
  if (n > MAX_WINDOW_DAYS) return `That is ${n} days - one run can read at most ${MAX_WINDOW_DAYS} (two fortnights and a margin)`;
  return null;
};

// Sync timestamps are naive UTC from the server (the same convention as the
// shift columns). Pin them to UTC and print NZ wall-clock, because an office
// reading "started 20:00" means NZ time. A value that already carries a zone
// is left alone rather than given a second one.
export const fmtSyncStamp = (iso) => {
  if (!iso) return "";
  const s = String(iso);
  const withTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`;
  try {
    const dt = new Date(withTz);
    if (Number.isNaN(dt.getTime())) return s;
    return dt.toLocaleString("en-NZ", {
      timeZone: "Pacific/Auckland", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return s; }
};

export const SYNC_TONE = {
  success: "text-emerald-300",
  partial: "text-amber-300",      // some drivers could not be read; the rest were written
  error: "text-red-300",
  running: "text-sky-300",
  idle: "text-slate-400",
};

// "29 Sep" - the day beside a time, where a full date would crowd the row.
const shortDay = (d) => {
  try {
    const dt = new Date(`${String(d).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString("en-NZ", { day: "2-digit", month: "short" });
  } catch { return d; }
};

// Log Ninja times arrive as NZ local "2026-09-29T06:16" - wall-clock already,
// no zone maths - and our own shift rows as the server's "HH:MM". Show HH:MM,
// and the day too when it is not the shift's own day, because a log that
// crosses midnight finishes "tomorrow" and "02:15" on its own reads as a
// four-hour day.
const clock = (v, day) => {
  if (!v) return "open";
  const s = String(v);
  if (!s.includes("T")) return s;
  const t = s.slice(11, 16);
  const d = s.slice(0, 10);
  return day && d !== day ? `${t} ${shortDay(d)}` : t;
};
const span = (start, finish, day) => `${clock(start, day)}–${clock(finish, day)}`;
const breakList = (breaks, day) =>
  ((breaks || []).length ? breaks.map((b) => span(b.start, b.finish, day)).join(", ") : "no break");
const minutesToHours = (m) => fmtHours((Number(m) || 0) / 60);

// Log Ninja's "work hours" are the driving/working time with the rests taken
// out, but the timesheet pays the whole span - breaks are PAID here (see
// timesheets/util.js) and "Ours" prints the server's paid_hours, which is
// finish minus start. Put beside each other, those two measures read as a
// disagreement that is not there, so the Log Ninja side shows the same span.
// The strings are NZ wall clock carrying their own date (a finish past
// midnight is on the next day), and the span is the real elapsed time in
// Pacific/Auckland rather than a wall-clock subtraction: the server pays the
// stored instants, and on the two clock-change nights a year the wall clock
// is an hour out from them. A browser with no zone data falls back to the
// wall clock, which is right every other night.
const NZ_TZ = "Pacific/Auckland";
let nzWallFmt = null;
const nzOffsetMs = (utcMs) => {
  nzWallFmt = nzWallFmt || new Intl.DateTimeFormat("en-US", {
    timeZone: NZ_TZ, hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });
  const p = {};
  nzWallFmt.formatToParts(new Date(utcMs)).forEach(({ type, value }) => { p[type] = value; });
  const wall = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute);
  return wall - utcMs;
};
// An NZ ISO minute ("2026-09-29T06:16") as ms since the epoch; NaN for a blank.
export const nzInstant = (iso) => {
  if (!iso) return NaN;
  const head = String(iso).trim().slice(0, 16).replace(" ", "T");
  const wall = Date.parse(`${head}:00Z`);         // the wall clock read as if it were UTC
  if (!Number.isFinite(wall)) return NaN;
  try {
    // The offset depends on the instant, which is what is being solved for:
    // take it at the wall-clock guess, then read it again at the corrected
    // instant so a time just past a clock change lands on the right side.
    const guess = wall - nzOffsetMs(wall);
    return wall - nzOffsetMs(guess);
  } catch { return wall; }
};
// Hours from start to finish to 2dp, rounded as the server's paid_hours is;
// null while the finish is still open (or not after the start).
export const spanHours = (start, finish) => {
  const a = nzInstant(start);
  const b = nzInstant(finish);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return null;
  return Math.round(((b - a) / 3600000) * 100) / 100;
};
const dayOf = (item) => String(item?.work_date || "").slice(0, 10);
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
const hoursMin = (m) => `${Math.floor((m || 0) / 60)}h${String((m || 0) % 60).padStart(2, "0")}`;

// The days a run read, as "30 Sep – 03 Oct". A record written before windows
// were stored only knows how many days back it looked, so it says that.
export const runWindow = (rec) => {
  if (!rec) return "";
  if (rec.since) return `${shortDay(rec.since)} – ${shortDay(rec.until || nzToday())}`;
  if (rec.days) return `last ${plural(rec.days, "day")}`;
  return "";
};

// "Reading driver 7 of 18 · 112 days read so far" from the progress the
// server lifts out of the fetcher's log lines; the fetcher's own note before
// the first driver ("logged in"); null when the run has not said anything.
export const progressLine = (prog) => {
  if (!prog) return null;
  const total = Number(prog.drivers_total) || 0;
  const done = Number(prog.drivers_done) || 0;
  const read = Number(prog.shifts_read) || 0;
  if (total > 0 && done > 0) return `${Math.min(done, total)} of ${total} drivers read · ${plural(read, "day")} read so far`;
  if (total > 0) return `Reading ${plural(total, "driver")}…`;
  return prog.note || null;
};
// Conflicts resolve by log_id; the other lists only need a stable key.
const itemKey = (item, i) => item.log_id || `${item.person_name || item.driver_name}-${item.work_date}-${item.log_name || i}`;

// The counts along the top. A preview has not created anything yet, so its
// chips say what WOULD happen; an applied run says what did.
const CHIPS = [
  { key: "create", label: (dry) => (dry ? "to create" : "created"), tone: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
  { key: "update", label: (dry) => (dry ? "to update" : "updated"), tone: "text-sky-300 border-sky-500/40 bg-sky-500/10" },
  { key: "unchanged", label: () => "unchanged", tone: "text-slate-400 border-slate-700 bg-slate-800/40" },
  { key: "conflicts", label: () => "conflicts", tone: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
  { key: "unmatched", label: () => "unmatched", tone: "text-rose-300 border-rose-500/40 bg-rose-500/10" },
  { key: "locked", label: () => "locked", tone: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
  { key: "open", label: () => "still working", tone: "text-sky-300 border-sky-500/40 bg-sky-500/10" },
  { key: "errors", label: () => "errors", tone: "text-red-300 border-red-500/40 bg-red-500/10" },
];

const ROW = "flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] rounded-lg border px-2.5 py-1.5";
const ROW_TONE = {
  plain: "border-slate-800 bg-slate-900/30",
  amber: "border-amber-500/30 bg-amber-500/5",
  rose: "border-rose-500/30 bg-rose-500/5",
  sky: "border-sky-500/30 bg-sky-500/5",
  red: "border-red-500/30 bg-red-500/5",
};
const BTN = "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all disabled:opacity-50";

// One logbook day on one line: who, when, the times, the breaks, the hours.
function LogRow({ item, tone, note }) {
  const day = dayOf(item);
  return (
    <div className={`${ROW} ${ROW_TONE[tone] || ROW_TONE.plain}`}>
      <span className="font-bold text-slate-100">{item.person_name || item.driver_name || "?"}</span>
      {day && <span className="text-slate-400">{prettyDate(day)}</span>}
      {item.start && <span className="font-mono text-slate-300">{span(item.start, item.finish, day)}</span>}
      {(item.breaks || []).length > 0 && <span className="text-slate-500">break {breakList(item.breaks, day)}</span>}
      {item.work_minutes != null && <span className="font-mono text-slate-500">{minutesToHours(item.work_minutes)} h</span>}
      {item.log_name && <span className="font-mono text-slate-600">{item.log_name}</span>}
      {note && <span className="text-slate-400 ml-auto">{note}</span>}
    </div>
  );
}

// A Log Ninja name nobody on the crew list answers to: pick the crew member
// it belongs to. The link is kept for every run after, and the days of the
// last run waiting under that name are written on the press.
// What the server wrote on the press, every bucket named: a day that landed
// as locked or in conflict is not "nothing to write".
function appliedText(applied) {
  if (!applied) return "";
  if (applied.preview_only != null) return " — press Apply to write the days";
  const parts = [];
  const written = (applied.create || 0) + (applied.update || 0);
  if (written) parts.push(`${plural(written, "day")} written`);
  if (applied.unchanged) parts.push(`${applied.unchanged} already on the timesheet`);
  if (applied.conflicts) parts.push(`${plural(applied.conflicts, "day")} in conflict with a day the office keyed in (see below)`);
  if (applied.locked) parts.push(`${plural(applied.locked, "day")} in an approved fortnight, not written`);
  if (applied.open) parts.push(`${applied.open} still open in Log Ninja`);
  if (applied.errors) parts.push(`${plural(applied.errors, "day")} unreadable`);
  return parts.length ? ` — ${parts.join(", ")}` : " — nothing to write";
}

function LinkDriver({ name, days, crew, crewError, onRetryCrew, canEdit, busy, result, onLink }) {
  const [pick, setPick] = useState("");
  const [again, setAgain] = useState(false);          // re-pick after a wrong link
  const showPicker = canEdit && (!result || again);
  return (
    <div className={`${ROW} ${ROW_TONE.rose}`}>
      <span className="font-bold text-slate-100">{name}</span>
      <span className="text-slate-400">{plural(days.length, "day")} in Log Ninja</span>
      {result && (
        <span className="text-emerald-300">
          linked to <span className="font-bold">{result.alias?.person_name}</span>{appliedText(result.applied)}
          {canEdit && !again && (
            <button type="button" onClick={() => setAgain(true)} disabled={busy}
              className="ml-2 text-[11px] text-slate-400 underline hover:text-white">wrong person?</button>
          )}
        </span>
      )}
      {showPicker && (
        <span className="flex items-center gap-1.5 ml-auto">
          {crewError ? (
            <span className="text-amber-300">could not load the crew list
              <button type="button" onClick={onRetryCrew} className="ml-1 underline hover:text-white">retry</button>
            </span>
          ) : (
            <select value={pick} onChange={(e) => setPick(e.target.value)} disabled={busy || !crew}
              className="bg-slate-950 border border-slate-700/80 rounded-lg px-2 py-1 text-[11px] text-slate-200 outline-none focus:border-indigo-500 disabled:opacity-50">
              <option value="">{crew ? "This is…" : "loading crew…"}</option>
              {(crew || []).map((c) => <option key={c.id} value={c.id}>{c.name}{c.role ? ` (${c.role})` : ""}</option>)}
            </select>
          )}
          <button type="button" onClick={() => { if (pick) { onLink(name, pick); setAgain(false); } }} disabled={busy || !pick}
            className={`${BTN} bg-indigo-600 hover:bg-indigo-500 text-white`}>Link</button>
        </span>
      )}
    </div>
  );
}

// Log Ninja's day beside ours, and the choice. Once a side is picked the
// buttons go dead: the server has written the answer and pressing the other
// one would not undo it, it would be a second edit.
function Conflict({ item, canEdit, busy, onResolve }) {
  const day = dayOf(item);
  const ours = item.existing || {};
  const resolved = item.resolved;
  // The same measure on both sides - the paid span. Log Ninja's own work
  // minutes leave the rests out, so they are not shown as hours here; the
  // rest total is labelled for what it is instead.
  const theirs = spanHours(item.start, item.finish);
  const rest = Number(item.rest_minutes) || 0;
  const ourBreaks = (ours.breaks || []).length
    ? breakList(ours.breaks, day)
    : ours.break_minutes ? `${ours.break_minutes} min (no times)` : "no break";
  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 space-y-1.5 text-[11px]">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-bold text-slate-100">{item.person_name || "?"}</span>
        <span className="text-slate-400">{prettyDate(day)}</span>
        {item.log_name && <span className="font-mono text-slate-600">{item.log_name}</span>}
        {resolved && (
          <span className="ml-auto text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded border text-emerald-300 border-emerald-500/40 bg-emerald-500/10">
            {resolved === "logninja" ? "using Log Ninja" : "kept ours"}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className="rounded-lg bg-slate-950/60 border border-slate-800 px-2.5 py-1.5">
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Log Ninja</div>
          <div className="font-mono text-slate-200">{span(item.start, item.finish, day)}{theirs != null && ` · ${fmtHours(theirs)} h`}</div>
          <div className="text-slate-500">break {breakList(item.breaks, day)}{rest > 0 && ` · rest ${rest} min`}</div>
        </div>
        <div className="rounded-lg bg-slate-950/60 border border-slate-800 px-2.5 py-1.5">
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">
            Ours{ours.source ? ` · ${ours.source === "manual" ? "keyed in" : ours.source}` : ""}
          </div>
          <div className="font-mono text-slate-200">{span(ours.start, ours.finish, day)} · {fmtHours(ours.paid_hours)} h</div>
          <div className="text-slate-500">break {ourBreaks}</div>
        </div>
      </div>
      {canEdit && (
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => onResolve(item, "logninja")} disabled={busy || !!resolved}
            className={`${BTN} bg-indigo-600 hover:bg-indigo-500 text-white`}>
            Use Log Ninja
          </button>
          <button type="button" onClick={() => onResolve(item, "keep")} disabled={busy || !!resolved}
            className={`${BTN} bg-slate-900/60 border border-slate-700 text-slate-200 hover:border-slate-500`}>
            Keep ours
          </button>
        </div>
      )}
    </div>
  );
}

// A list the office only needs on a bad day, folded by default.
function Folded({ title, items, open, onToggle, tone }) {
  if (!items.length) return null;
  return (
    <div className="space-y-1">
      <button type="button" onClick={onToggle}
        className={`flex items-center gap-1.5 text-[11px] font-bold ${tone || "text-slate-300"} hover:text-white`}>
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        {title} <span className="font-mono font-normal text-slate-500">({items.length})</span>
      </button>
      {open && <div className="space-y-1">{items.map((it, i) => <LogRow key={itemKey(it, i)} item={it} />)}</div>}
    </div>
  );
}

export default function LogNinjaSync({ onApplied }) {
  const canEdit = useCanEdit();
  const [status, setStatus] = useState(null);      // GET /time/logninja/status, as the server last said it
  const [hidden, setHidden] = useState(false);     // 403: this role is not shown the panel at all
  const [days, setDays] = useState(3);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState({});            // which folded lists (and the past-days box) are unfolded
  const [resolving, setResolving] = useState({});  // log_id -> true while its resolve is in flight
  // The schedule switch. null until the server has said which way it is: a
  // switch drawn from a guess invites a click that "changes" nothing.
  const [mode, setMode] = useState(null);          // "auto" (live) | "manual" (on hold)
  const [modeMeta, setModeMeta] = useState(null);  // {changed_at, changed_by} from the last PUT
  const [modeBusy, setModeBusy] = useState(false);
  const [periods, setPeriods] = useState([]);      // GET /time/periods?count=6 - the fortnights the presets are cut from
  const [win, setWin] = useState({ since: "", until: "" });   // the "Get past days" window

  const running = !!status?.running;
  const wasRunning = useRef(false);
  // The parent passes an inline arrow, so the latest one lives in a ref and
  // the poll is not re-armed every render.
  const appliedRef = useRef(onApplied);
  useEffect(() => { appliedRef.current = onApplied; }, [onApplied]);

  // The mode rides on /status (top level, and on the running record) and on
  // /login. Take it from whichever answered: the status poll keeps the switch
  // honest when it is flipped from another tab, the login read shows it on a
  // server build whose status does not carry it yet.
  const takeMode = useCallback((data) => {
    const m = data?.mode ?? data?.last_sync?.mode;
    if (m === "auto" || m === "manual") setMode(m);
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const res = await API.get("/time/logninja/status");
      setStatus(res.data || { running: false, last_sync: null });
      takeMode(res.data);
    } catch (err) {
      // 403: this role is not shown the panel; 404: the server does not have
      // the Log Ninja module yet (the frontend can ship ahead of it)
      if (err?.response?.status === 403 || err?.response?.status === 404) setHidden(true);
      else setError(errMsg(err));
    }
  }, [takeMode]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Once the first status has answered - so a role the panel hides never
  // makes these calls - read the mode from the login and the three
  // fortnights the past-days presets are cut from. The presets are a
  // convenience (the From/To inputs do the same job), so a failed periods
  // read is not an error the office needs to see.
  const extrasLoaded = useRef(false);
  const modeSeq = useRef(0);          // bumped on every switch press; stale mode answers are ignored
  useEffect(() => {
    if (!status || extrasLoaded.current) return;
    extrasLoaded.current = true;
    // The login read is only a fallback for a server whose status carries no
    // mode; and a slow answer must never undo a switch flipped in the meantime.
    if ((status?.mode ?? status?.last_sync?.mode) == null) {
      const seq = modeSeq.current;
      API.get("/time/logninja/login").then((res) => { if (modeSeq.current === seq) takeMode(res.data); }).catch(() => {});
    }
    API.get("/time/periods?count=6").then((res) => {
      const rows = Array.isArray(res.data) ? res.data : [];
      setPeriods(rows);
      // Open on "this fortnight" unless the office has already typed a window.
      if (rows[0]?.start) setWin((w) => (w.since || w.until ? w : { since: rows[0].start, until: nzToday() }));
    }).catch(() => {});
  }, [status, takeMode]);

  // Poll while the server says a run is on. The moment it stops, the grid
  // behind this panel is stale - a run that wrote nothing is still a run the
  // office pressed, and reloading is cheap.
  useEffect(() => {
    if (!running) {
      if (wasRunning.current) {
        wasRunning.current = false;
        if (appliedRef.current) appliedRef.current();
      }
      return undefined;
    }
    wasRunning.current = true;
    const t = setInterval(loadStatus, POLL_MS);
    return () => clearInterval(t);
  }, [running, loadStatus]);

  // Start a run - `body` is {days} or {since, until} plus commit, `ask` the
  // confirmation an apply needs (a preview has none).
  const start = async (body, ask) => {
    if (ask && !window.confirm(ask)) return;
    setBusy(true);
    setError(null);
    try {
      const res = await API.post("/time/logninja/sync", body);
      // "skipped" is what the schedule hears while on hold. The office's own
      // press always runs, so this only guards a server that says it anyway
      // - and then the reason is worth more than a spinner that never ends.
      if (res.data?.status === "skipped") { setError(res.data.reason || "The run was skipped"); return; }
      // 202: the run is the server's now. Flag it running so the poll starts
      // at once rather than waiting for the next status read.
      setStatus((s) => ({ ...(s || {}), running: true }));
    } catch (err) {
      setError(errMsg(err));
      // 409: a run is already on - follow that one instead.
      if (err?.response?.status === 409) setStatus((s) => ({ ...(s || {}), running: true }));
    } finally {
      setBusy(false);
    }
  };

  const startSync = (commit) => start({ days, commit }, commit && (
    `Apply the last ${plural(days, "day")} from Log Ninja?\n\n`
    + "This will update the drivers' hours from Log Ninja: the days they signed there are "
    + "written onto the timesheet. A day that disagrees with what is already here is listed "
    + "as a conflict for you to decide, and an approved fortnight is never touched."));

  const startWindow = (commit) => start({ since: win.since, until: win.until, commit }, commit && (
    `Apply ${prettyDate(win.since)} to ${prettyDate(win.until)} from Log Ninja?\n\n`
    + `This will write ${plural(windowLength(win.since, win.until), "day")} of logbook hours from Log Ninja `
    + "onto the timesheet. Days the office edited stay as conflicts. Days in an approved fortnight are not written."));

  // The schedule switch. Confirmed both ways: Live means writes happen with
  // nobody watching, On hold means a day signed on the phone stops arriving.
  const switchMode = async () => {
    const next = mode === "auto" ? "manual" : "auto";
    const ask = next === "auto"
      ? "Turn the automatic Log Ninja sync on?\n\n"
        + "The scheduled runs at 4 am and 12 pm (NZ time) will apply the last 3 days of logbook hours "
        + "onto the timesheet every day. Days the office edited still come back as conflicts."
      : "Put the automatic Log Ninja sync on hold?\n\n"
        + "The 4 am and 12 pm runs are skipped until it is switched back to Live. "
        + "Preview, Apply and Get past days keep working from here.";
    if (!window.confirm(ask)) return;
    setModeBusy(true);
    setError(null);
    try {
      const res = await API.put("/time/logninja/mode", { mode: next });
      modeSeq.current += 1;
      takeMode(res.data?.mode ? res.data : { mode: next });
      setModeMeta({ changed_at: res.data?.changed_at || null, changed_by: res.data?.changed_by || null });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setModeBusy(false);
    }
  };

  // "Link" on a name the crew list does not know: PUT the alias, then show
  // what the server wrote from the last run's saved days.
  const [crew, setCrew] = useState(null);
  const [crewError, setCrewError] = useState(false);
  const [linking, setLinking] = useState({});
  const [linked, setLinked] = useState({});          // name -> the server's answer to this session's Link
  const [aliases, setAliases] = useState(null);      // GET /time/logninja/aliases: links from earlier sessions
  const needCrew = (status?.last_sync?.report?.unmatched || []).length > 0;
  useEffect(() => {
    if (!needCrew || crew !== null || crewError) return;
    let alive = true;
    API.get("/drivers").then((res) => {
      if (!alive) return;
      const rows = (Array.isArray(res.data) ? res.data : res.data?.drivers || [])
        .map((d) => ({ id: d.id, name: d.name, role: d.role }))
        .sort((a, b) => String(a.name).localeCompare(String(b.name)));
      setCrew(rows);
    }).catch(() => { if (alive) setCrewError(true); });
    API.get("/time/logninja/aliases").then((res) => { if (alive) setAliases(res.data?.aliases || []); }).catch(() => {});
    return () => { alive = false; };
  }, [needCrew, crew, crewError]);
  const retryCrew = () => { setCrewError(false); setCrew(null); };
  // a finished run re-lists what is still unmatched: this session's answers
  // no longer describe it
  useEffect(() => { if (!running) { setLinked({}); setAliases(null); } }, [running]);
  const link = async (name, personId) => {
    setLinking((l) => ({ ...l, [name]: true }));
    setError(null);
    try {
      const res = await API.put("/time/logninja/aliases", { driver_name: name, person_id: personId });
      setLinked((l) => ({ ...l, [name]: res.data }));
      const a = res.data?.applied;
      if (a && ((a.create || 0) + (a.update || 0)) > 0 && appliedRef.current) appliedRef.current();
      loadStatus();                                   // the per-day rows now carry the link
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLinking((l) => ({ ...l, [name]: false }));
    }
  };

  const resolve = async (item, choice) => {
    const id = item.log_id;
    setResolving((r) => ({ ...r, [id]: true }));
    setError(null);
    try {
      const res = await API.post("/time/logninja/resolve", { log_id: id, choice });
      const saved = res.data?.item || { ...item, resolved: choice };
      setStatus((s) => {
        const report = s?.last_sync?.report;
        if (!report) return s;
        const conflicts = (report.conflicts || []).map((c) =>
          (c.log_id === id ? { ...c, ...saved, resolved: saved.resolved || choice } : c));
        return { ...s, last_sync: { ...s.last_sync, report: { ...report, conflicts } } };
      });
      // Taking Log Ninja's day changed the grid; keeping ours changed nothing.
      if (choice === "logninja" && appliedRef.current) appliedRef.current();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setResolving((r) => {
        const next = { ...r };
        delete next[id];
        return next;
      });
    }
  };

  if (hidden) return null;

  const last = status?.last_sync || null;
  const report = last?.report || {};
  const summary = last?.summary || {};
  const dry = !!last?.dry_run;
  const list = (k) => (Array.isArray(report[k]) ? report[k] : []);
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  const conflicts = list("conflicts");
  const unmatched = list("unmatched");
  const locked = list("locked");
  const stillOpen = list("open");
  const errors = list("errors");
  const fetchFailures = list("fetch_failures");
  const notShared = list("not_shared");          // work periods with hours in no log shared with DAV
  const partial = last?.status === "partial" || fetchFailures.length > 0;
  // Beside the status: the window the running or last run read, and while a
  // run is on, how far through the drivers it is.
  // while a run is starting, "last" is still the previous run until the
  // first poll answers: show its window and progress only once the record
  // itself says running
  const live = running && last?.status === "running";
  const lastWindow = (live || !running) ? runWindow(last) : null;
  const progressText = live ? progressLine(last?.progress) : null;
  // "Get past days": the presets are cut from the server's fortnights (its
  // anchor, not ours - see timesheets/util.js) and the window is checked
  // here before the buttons go live.
  const today = nzToday();
  // the operator's presets (6 Oct 2026): the last 3, 7, 14 or 28 days ending
  // today; the fortnight rows below still tell the office which of those
  // days sit in an approved pay period
  const presets = PAST_DAY_PRESETS.map((n) => ({ key: String(n), label: `Last ${n} days`, ...lastDays(n, today) }));
  const problem = windowProblem(win.since, win.until, today);
  const winDays = problem ? 0 : windowLength(win.since, win.until);
  // An approved fortnight inside the window is not written (its days come
  // back as locked) - say so before the press, not after.
  const approvedInWindow = problem ? [] : periods.filter((p) => p.status === "approved" && p.start <= win.until && p.end >= win.since);
  const modeTitle = modeMeta?.changed_at
    ? `Switched${modeMeta.changed_by ? ` by ${modeMeta.changed_by}` : ""} ${fmtSyncStamp(modeMeta.changed_at)} - click to switch`
    : mode === "auto" ? "Click to put the 4 am and 12 pm runs on hold" : "Click to turn the 4 am and 12 pm runs on";

  return (
    <div className="bg-slate-950/40 rounded-2xl border border-slate-800/80 p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="flex items-center gap-2 text-sm font-bold text-white">
            <BookOpen size={16} className="text-indigo-400" /> Log Ninja sync
          </span>
          {running ? (
            <span className="flex flex-wrap items-center gap-1.5 text-sky-300">
              <RefreshCw size={12} className="animate-spin" />
              {progressText || `running${last?.started_at && last.status === "running" ? ` since ${fmtSyncStamp(last.started_at)}` : "…"}`}
              {lastWindow && <span className="text-slate-500">· {lastWindow}</span>}
            </span>
          ) : last ? (
            <span className="text-slate-400">
              last {dry ? "preview" : "sync"} {fmtSyncStamp(last.finished_at || last.started_at)}
              {lastWindow && ` · ${lastWindow}`} ·{" "}
              <span className={SYNC_TONE[last.status] || SYNC_TONE.idle}>{last.status}</span>
            </span>
          ) : (
            <span className="text-slate-500">never run</span>
          )}
        </div>

        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            {/* The schedule switch. Hidden until the server has said the
                mode, which also means it understands the window contract
                below. "On hold" is amber rather than red: it is the state
                the operator asked to start in, not a fault. */}
            {mode && (
              <button type="button" role="switch" aria-checked={mode === "auto"} onClick={switchMode} disabled={modeBusy}
                title={modeTitle}
                className={`${BTN} border ${mode === "auto"
                  ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300 hover:border-emerald-400"
                  : "bg-slate-900/60 border-slate-700 text-amber-300 hover:border-slate-500"}`}>
                {mode === "auto" ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                <span className="font-normal text-slate-400">Automatic daily sync</span>
                {mode === "auto" ? "Live · 4 am and 12 pm" : "On hold"}
              </button>
            )}
            {mode && <span className="hidden sm:block w-px h-5 bg-slate-800" aria-hidden="true" />}
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} disabled={busy || running}
              title="How far back to read the drivers' logbooks"
              className="bg-slate-950 border border-slate-700/80 rounded-lg px-2 py-1.5 text-[11px] text-slate-200 outline-none focus:border-indigo-500 cursor-pointer disabled:opacity-50">
              {DAY_CHOICES.map((n) => <option key={n} value={n}>last {plural(n, "day")}</option>)}
            </select>
            <button type="button" onClick={() => startSync(false)} disabled={busy || running}
              className={`${BTN} bg-slate-900/60 border border-slate-800 text-slate-200 hover:border-slate-600`}
              title="Read Log Ninja and show what would change - nothing is written">
              <RefreshCw size={13} /> Preview (last {plural(days, "day")})
            </button>
            <button type="button" onClick={() => startSync(true)} disabled={busy || running}
              className={`${BTN} bg-indigo-600 hover:bg-indigo-500 text-white`}
              title="Write the drivers' logbook days onto the timesheet">
              <Play size={13} /> Apply
            </button>
          </div>
        )}
      </div>

      {mode === "manual" && (
        <p className="text-[11px] text-slate-500 flex items-center gap-1.5">
          <PauseCircle size={12} className="shrink-0" /> Scheduled runs are skipped; use Preview or Get past days
        </p>
      )}

      {/* A wider window than the quick controls offer, for the days the
          schedule never covered. Only on a server that says its mode: one
          from before the window contract would read {since, until} as "the
          last 3 days" and apply THAT - the same ship-ahead rule as the 404
          above. Folded by default: the daily view is the results. */}
      {canEdit && mode && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/30">
          <button type="button" onClick={() => toggle("window")} aria-expanded={!!open.window}
            className="w-full flex flex-wrap items-center gap-2 px-3 py-2 text-[11px] text-left text-slate-400 hover:text-white">
            {open.window ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <HistoryIcon size={13} className="text-indigo-400" />
            <span className="font-bold text-slate-200">Get past days</span>
            <span className="text-slate-500">— read the last 3, 7, 14 or 28 days from Log Ninja, or any dates up to {MAX_WINDOW_DAYS} days; preview first, then apply</span>
          </button>
          {open.window && (
            <div className="px-3 pb-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                {presets.map((p) => {
                  const on = win.since === p.since && win.until === p.until;
                  return (
                    <button key={p.key} type="button" onClick={() => setWin({ since: p.since, until: p.until })}
                      aria-pressed={on} disabled={busy || running}
                      title={`${prettyDate(p.since)} to ${prettyDate(p.until)}`}
                      className={`${BTN} border ${on
                        ? "bg-slate-700 text-white border-slate-600"
                        : "bg-slate-900/60 text-slate-300 border-slate-700 hover:border-slate-500"}`}>
                      {p.label}
                    </button>
                  );
                })}
                {presets.length > 0 && <span className="hidden sm:block w-px h-5 bg-slate-800" aria-hidden="true" />}
                <label className="flex items-center gap-1.5 text-[11px] text-slate-400">
                  From
                  <input type="date" value={win.since} max={win.until || today} disabled={busy || running}
                    onChange={(e) => setWin((w) => ({ ...w, since: e.target.value }))}
                    className="bg-slate-950 border border-slate-700/80 rounded-lg px-2 py-1.5 text-slate-200 outline-none focus:border-indigo-500 disabled:opacity-50" />
                </label>
                <label className="flex items-center gap-1.5 text-[11px] text-slate-400">
                  To
                  <input type="date" value={win.until} min={win.since || undefined} max={today} disabled={busy || running}
                    onChange={(e) => setWin((w) => ({ ...w, until: e.target.value }))}
                    className="bg-slate-950 border border-slate-700/80 rounded-lg px-2 py-1.5 text-slate-200 outline-none focus:border-indigo-500 disabled:opacity-50" />
                </label>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-[11px]">
                {problem ? (
                  <span className="flex items-center gap-1.5 text-amber-300"><AlertTriangle size={12} className="shrink-0" /> {problem}</span>
                ) : (
                  <span className="text-slate-300">
                    From <span className="font-bold text-slate-100">{prettyDate(win.since)}</span> to{" "}
                    <span className="font-bold text-slate-100">{prettyDate(win.until)}</span> · {plural(winDays, "day")}
                    {approvedInWindow.length > 0 && (
                      <span className="text-amber-300">
                        {" "}· {plural(approvedInWindow.length, "approved fortnight")} in this window - those days come back as locked, not written
                      </span>
                    )}
                  </span>
                )}
                <span className="ml-auto flex items-center gap-2">
                  <button type="button" onClick={() => startWindow(false)} disabled={busy || running || !!problem}
                    className={`${BTN} bg-slate-900/60 border border-slate-800 text-slate-200 hover:border-slate-600`}
                    title="Read Log Ninja for these days and show what would change - nothing is written">
                    <RefreshCw size={13} /> Preview window
                  </button>
                  <button type="button" onClick={() => startWindow(true)} disabled={busy || running || !!problem}
                    className={`${BTN} bg-indigo-600 hover:bg-indigo-500 text-white`}
                    title="Write these days' logbook hours onto the timesheet">
                    <Play size={13} /> Apply window
                  </button>
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {last?.message && (
        <p className={`text-[11px] ${last.status === "error" ? "text-red-300" : "text-slate-400"}`}>{last.message}</p>
      )}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-xs text-red-300 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span className="flex-1 break-words">{error}</span>
          <button type="button" onClick={() => setError(null)} className="text-red-300/70 hover:text-white"><X size={13} /></button>
        </div>
      )}

      {/* A driver the portal would not give up (a timeout, a page that never
          rendered) does not fail the run: the others' days are written and
          the run ends "partial". But that driver's days are NOT on the
          timesheet, and a quiet "2 created" below would read as a full
          morning - so the names go here, where the office will see whose
          week to check. */}
      {partial && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 text-xs text-amber-200 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-300" />
          <div className="flex-1 min-w-0 space-y-1">
            <div className="font-bold text-amber-300">
              {fetchFailures.length ? plural(fetchFailures.length, "driver") : "Some drivers"} could not be read from Log Ninja this run
              {" — "}{dry ? "their days are not in this preview" : "their days were not written"}
            </div>
            {fetchFailures.length > 0 && (
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px]">
                {fetchFailures.map((f, i) => (
                  <span key={`${f.driver || "?"}-${i}`} title={[f.stage, f.error].filter(Boolean).join(": ")}>
                    <span className="font-bold text-slate-100">{f.driver || "?"}</span>
                    {f.error && <span className="text-slate-400"> · {f.stage ? `${f.stage}: ` : ""}{f.error}</span>}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hours Log Ninja counts for a driver but hands over in no day log.
          The driver page's Work Hours is the whole cumulative work period -
          every company, every day - while the log list and the period's
          Export hold only the logs assigned to DAV Transport. The sync
          cannot import a log the portal withholds, so it names the gap
          here; the fix is in the driver's app, not on the dashboard. */}
      {notShared.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 text-xs text-amber-200 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-300" />
          <div className="flex-1 min-w-0 space-y-1">
            <div className="font-bold text-amber-300">
              Hours in Log Ninja that are in no log shared with DAV — {plural(new Set(notShared.map((h) => h.driver)).size, "driver")},{" "}
              {hoursMin(notShared.reduce((n, h) => n + (h.hidden_minutes || 0), 0))} in total
            </div>
            <div className="text-[11px] text-slate-300">
              Log Ninja counts these hours in the driver's work period, but the day log is not assigned to DAV Transport in the
              driver's app (or was never submitted), so neither the portal's log list nor its Export hands it over and the
              dashboard cannot import it. Ask the driver to put those days under DAV Transport in Log Ninja, then run the days again;
              until then the day keeps whatever the clock app or the office recorded.
            </div>
            <div className="space-y-0.5 text-[11px]">
              {notShared.map((h, i) => (
                <div key={`${h.driver || "?"}-${h.period_start || i}`}>
                  <span className="font-bold text-slate-100">{h.driver || "?"}</span>
                  <span className="text-slate-400">
                    {" "}· work period {shortDay(h.period_start)} – {shortDay(h.period_end) || "open"}: {hoursMin(h.period_minutes)} in Log Ninja,{" "}
                    {hoursMin(h.shared_minutes)} in the {plural(h.logs_listed || 0, "log")} shared with DAV
                    {h.log_names?.length ? ` (${h.log_names.join(", ")})` : ""} —{" "}
                  </span>
                  <span className="font-bold text-amber-200">{hoursMin(h.hidden_minutes)} not shared</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* A partial run counts like a success here: the chips say what WAS
          written; the notice above says whose days are missing. */}
      {last && (
        <div className="flex flex-wrap items-center gap-1.5">
          {CHIPS.map((c) => (
            <span key={c.key} className={`text-[10px] font-bold px-2 py-0.5 rounded border ${summary[c.key] ? c.tone : "text-slate-600 border-slate-800 bg-transparent"}`}>
              {summary[c.key] || 0} {c.label(dry)}
            </span>
          ))}
        </div>
      )}

      {/* The lists that need the office: a decision, a crew record, or a
          reopened fortnight. Always unfolded - folding them is how they get
          missed. */}
      {conflicts.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[11px] font-bold text-amber-300">
            Conflicts ({conflicts.length}) <span className="font-normal text-slate-400">— Log Ninja and the timesheet disagree; pick a side for each day</span>
          </div>
          {conflicts.map((c, i) => (
            <Conflict key={itemKey(c, i)} item={c} canEdit={canEdit} busy={!!resolving[c.log_id] || running} onResolve={resolve} />
          ))}
        </div>
      )}

      {unmatched.length > 0 && (() => {
        const names = [...new Set(unmatched.map((u) => u.driver_name || "?"))];
        const normName = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
        // what the panel knows of a name's link: this session's press first,
        // then the saved report's stamp, then an alias from an earlier session
        const knownLink = (name) => {
          if (linked[name]) return linked[name];
          const stamped = unmatched.find((u) => (u.driver_name || "?") === name && u.linked);
          if (stamped) return { alias: { person_name: stamped.linked.person_name }, applied: null };
          const earlier = (aliases || []).find((a) => normName(a.driver_name) === normName(name));
          if (earlier) return { alias: { person_name: earlier.person_name || "(no longer on the crew list)" }, applied: { preview_only: 0 } };
          return null;
        };
        return (
          <div className="space-y-1">
            <div className="text-[11px] font-bold text-rose-300 flex items-center gap-1.5">
              <UserX size={12} /> Log Ninja names not on the crew list ({plural(names.length, "name")}, {plural(unmatched.length, "day")})
              <span className="font-normal text-slate-400">— pick the crew member each name belongs to (kept for every run after), or add the person in Settings › crew</span>
            </div>
            {names.map((name) => (
              <LinkDriver key={name} name={name} days={unmatched.filter((u) => (u.driver_name || "?") === name)}
                crew={crew} crewError={crewError} onRetryCrew={retryCrew} canEdit={canEdit}
                busy={!!linking[name] || running} result={knownLink(name)} onLink={link} />
            ))}
            {unmatched.map((u, i) => <LogRow key={itemKey(u, i)} item={u} tone="rose"
              note={u.linked ? `linked to ${u.linked.person_name}` : (u.outcome ? `${u.outcome} - see that list` : u.reason)} />)}
          </div>
        );
      })()}

      {locked.length > 0 && (
        <div className="space-y-1">
          <div className="text-[11px] font-bold text-amber-300 flex items-center gap-1.5">
            <Lock size={12} /> Locked ({locked.length})
            <span className="font-normal text-slate-400">— not written; reopen the fortnight to change these days</span>
          </div>
          {locked.map((l, i) => <LogRow key={itemKey(l, i)} item={l} tone="amber" note="fortnight approved" />)}
        </div>
      )}

      {stillOpen.length > 0 && (
        <div className="space-y-1">
          <div className="text-[11px] font-bold text-sky-300 flex items-center gap-1.5">
            <Clock size={12} /> Still working ({stillOpen.length})
            <span className="font-normal text-slate-400">— the day is read once the driver signs off in Log Ninja</span>
          </div>
          {stillOpen.map((o, i) => <LogRow key={itemKey(o, i)} item={o} tone="sky" note="still working" />)}
        </div>
      )}

      {errors.length > 0 && (
        <div className="space-y-1">
          <div className="text-[11px] font-bold text-red-300 flex items-center gap-1.5">
            <AlertTriangle size={12} /> Errors ({errors.length})
          </div>
          {/* The importer puts what was wrong in `error`; a row read from an
              older report may say `message`. Without either shown the row is
              a name and a date with no clue what to fix. */}
          {errors.map((e, i) => <LogRow key={itemKey(e, i)} item={e} tone="red" note={e.error || e.message} />)}
        </div>
      )}

      {(list("create").length + list("update").length + list("unchanged").length) > 0 && (
        <div className="space-y-1.5 pt-1 border-t border-slate-800">
          <Folded title={dry ? "Would create" : "Created"} items={list("create")} open={!!open.create} onToggle={() => toggle("create")} tone="text-emerald-300" />
          <Folded title={dry ? "Would update" : "Updated"} items={list("update")} open={!!open.update} onToggle={() => toggle("update")} tone="text-sky-300" />
          <Folded title="Unchanged" items={list("unchanged")} open={!!open.unchanged} onToggle={() => toggle("unchanged")} />
        </div>
      )}

      {!last && !running && (
        <p className="text-[11px] text-slate-500">
          Reads each driver's signed logbook days from Log Ninja (start, finish and rest breaks) and writes them here.
          Preview first: it shows what would change without touching the timesheet.
        </p>
      )}
    </div>
  );
}
